import { ICatalogRow } from '../models/catalog';

export function parseCatalogCsv(content: string): string[][] {
  const normalized = content.replace(/^\uFEFF/, '');
  const rows: string[][] = [];
  let current = '';
  let row: string[] = [];
  let inQuotes = false;

  for (let i = 0; i < normalized.length; i += 1) {
    const char = normalized[i];
    const next = normalized[i + 1];

    if (char === '"' && inQuotes && next === '"') {
      current += '"';
      i += 1;
      continue;
    }

    if (char === '"') {
      inQuotes = !inQuotes;
      continue;
    }

    if (char === ',' && !inQuotes) {
      row.push(current);
      current = '';
      continue;
    }

    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && next === '\n') {
        i += 1;
      }
      row.push(current);
      if (row.some((cell) => cell.trim() !== '')) {
        rows.push(row);
      }
      row = [];
      current = '';
      continue;
    }

    current += char;
  }

  if (current.length > 0 || row.length > 0) {
    row.push(current);
    if (row.some((cell) => cell.trim() !== '')) {
      rows.push(row);
    }
  }

  return rows;
}

export function escapeCatalogCsvValue(value: string): string {
  if (value.includes('"')) {
    value = value.replace(/"/g, '""');
  }
  if (value.includes(',') || value.includes('\n') || value.includes('\r')) {
    return `"${value}"`;
  }
  return value;
}

export function normalizeCatalogHeader(value: string): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');
}

export function normalizeCatalogComparableValue(value: string): string {
  return normalizeCatalogHeader(value)
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function splitCatalogMultiValue(value: string | undefined | null): string[] {
  if (!value) {
    return [];
  }

  const text = String(value).trim();
  if (!text) {
    return [];
  }

  const values: string[] = [];
  let currentValue = '';
  let parenthesisDepth = 0;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];

    if (char === '(') {
      parenthesisDepth += 1;
      currentValue += char;
      continue;
    }

    if (char === ')') {
      parenthesisDepth = Math.max(parenthesisDepth - 1, 0);
      currentValue += char;
      continue;
    }

    if (char === ',' && parenthesisDepth === 0) {
      const normalizedValue = currentValue.trim();
      if (normalizedValue) {
        values.push(normalizedValue);
      }
      currentValue = '';
      continue;
    }

    currentValue += char;
  }

  const normalizedValue = currentValue.trim();
  if (normalizedValue) {
    values.push(normalizedValue);
  }

  return values;
}

export function getFirstFilledCatalogValue(...values: Array<string | undefined | null>): string {
  for (const value of values) {
    const normalized = String(value || '').trim();
    if (normalized) {
      return normalized;
    }
  }
  return '';
}

export function mapServerCatalogMultiValueToArray(value: string[] | string | undefined | null): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || '').trim()).filter((item) => item.length > 0);
  }
  return splitCatalogMultiValue(value ? String(value) : '');
}

export function mapCatalogRoutineToRoutineInformation(value: string[] | string | undefined | null): string {
  const routineValues = mapServerCatalogMultiValueToArray(value);
  if (!routineValues.length) {
    return '';
  }

  const normalizedValues = routineValues
    .map((routine) => normalizeCatalogHeader(routine).replace(/_/g, ' '))
    .filter((routine) => routine.length > 0);

  const hasMorning = normalizedValues.some((routine) => routine === 'morning' || routine === 'manha');
  const hasNight = normalizedValues.some((routine) => routine === 'night' || routine === 'noite');

  if (hasMorning && hasNight) {
    return 'Manhã e Noite';
  }
  if (hasMorning) {
    return 'Manhã';
  }
  if (hasNight) {
    return 'Noite';
  }

  return getFirstFilledCatalogValue(routineValues[0]);
}

export function mapCatalogCategoriesToCategory(value: string[] | string | undefined | null): string {
  const categories = mapServerCatalogMultiValueToArray(value);
  if (!categories.length) {
    return '';
  }
  return categories.join(', ');
}

export function mapCatalogWeightToString(value: number | string | undefined | null): string {
  if (value === undefined || value === null) {
    return '';
  }

  const normalized = String(value).trim().replace(',', '.');
  if (!normalized) {
    return '';
  }

  const parsed = Number(normalized);
  if (Number.isNaN(parsed)) {
    return normalized;
  }

  return String(parsed);
}

/**
 * Normaliza o preço para o formato que a company-api lê ("1234.56"). Aceita
 * "R$ 1.234,56", "89,90" e número do xlsx. Com vírgula, ponto é milhar; sem
 * vírgula, ponto é decimal. Texto que não vira número fica como veio, para a
 * validação apontar.
 */
export function mapCatalogPriceToString(value: number | string | undefined | null): string {
  if (value === undefined || value === null) {
    return '';
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '';
  }

  let normalized = value.replace(/R\$/gi, '').replace(/\s/g, '');
  if (!normalized) {
    return '';
  }

  if (normalized.includes(',')) {
    normalized = normalized.replace(/\./g, '').replace(',', '.');
  }

  // Mais de 2 casas fica como veio para a validação barrar: "1.000" viraria 1,
  // e em BRL quem escreve isso quis mil (mesma regra da company-api).
  if (/\.\d{3,}$/.test(normalized)) {
    return value.trim();
  }

  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? value.trim() : String(parsed);
}

/** Vazio é válido (produto sem preço). Senão: número, não negativo, até 2 casas. */
export function isCatalogPriceValid(value: string): boolean {
  const normalized = mapCatalogPriceToString(value);
  if (!normalized) {
    return true;
  }

  return /^\d+(\.\d{1,2})?$/.test(normalized);
}

export function getCatalogPriceLabel(value: string): string {
  const normalized = mapCatalogPriceToString(value);
  if (!normalized || !isCatalogPriceValid(normalized)) {
    return '-';
  }

  return Number(normalized).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function mapCatalogRangeToString(value: number[] | string | undefined | null): string {
  if (Array.isArray(value)) {
    if (value.length < 2) {
      return '';
    }
    const start = Number(value[0]);
    const end = Number(value[1]);
    if (Number.isNaN(start) || Number.isNaN(end)) {
      return '';
    }
    return `${start}-${end}`;
  }

  const text = String(value || '').trim();
  if (!text) {
    return '';
  }

  const normalized = text.replace(/\s+/g, '');
  const arrayFormat = normalized.match(/^\[(\d{1,3}),(\d{1,3})\]$/);
  if (arrayFormat) {
    return `${arrayFormat[1]}-${arrayFormat[2]}`;
  }

  return normalized;
}

export function isCatalogRangeValid(value: string): boolean {
  const trimmed = String(value || '').trim();
  if (!trimmed) {
    return true;
  }
  const match = trimmed.match(/^(\d{1,3})-(\d{1,3})$/);
  if (!match) {
    return false;
  }
  const start = Number(match[1]);
  const end = Number(match[2]);
  if (Number.isNaN(start) || Number.isNaN(end)) {
    return false;
  }
  if (start > end) {
    return false;
  }
  if (start < 0 || end < 0) {
    return false;
  }
  if (start > 100 || end > 100) {
    return false;
  }
  return true;
}

export function isCatalogWeightValid(value: string, minWeight: number, maxWeight: number): boolean {
  const trimmed = String(value || '').trim();
  if (!trimmed) {
    return true;
  }

  const normalized = trimmed.replace(',', '.');
  const parsed = Number(normalized);
  if (Number.isNaN(parsed)) {
    return false;
  }

  return parsed >= minWeight && parsed <= maxWeight;
}

export function getCatalogWeightLabel(value: string): string {
  const normalized = mapCatalogWeightToString(value);
  if (normalized) {
    return normalized;
  }
  return 'Sem peso';
}

export function getCatalogWeightRangeValue(value: string, minWeight: number, maxWeight: number): number {
  const normalized = mapCatalogWeightToString(value);
  const parsed = Number(normalized);

  if (Number.isNaN(parsed)) {
    return minWeight;
  }

  if (parsed < minWeight) {
    return minWeight;
  }

  if (parsed > maxWeight) {
    return maxWeight;
  }

  return parsed;
}

export function rowHasCatalogData(row: ICatalogRow): boolean {
  return Object.values(row).some((value) => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return String(value || '').trim().length > 0;
  });
}
